/* eslint-disable camelcase */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { clearSearchHistory, getSearchHistory } from "@insite/client-framework/Services/AutocompleteService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Clock from "@insite/mobius/Icons/Clock";
import { ButtonIcon } from "@insite/mobius/Button";
import { StyledButton } from "@insite/mobius/Clickable";
import SearchIcon from "@NA/Icons/SearchIcon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useCoveoSearch from "@NA/Components/Coveo/Commerce/useCoveoSearch";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { HasCoveoHooks, withCoveoHooks } from "@NA/Components/Coveo/Contexts/CoveoHooksContext";

interface OwnProps {
    focusedQuery?: string;
    clearSearch: () => void;
    closeAutocomplete: () => void;
    extendedStyles?: SearchHistoryStyles;
}

const mapStateToProps = (state: CustomApplicationState) => ({
    searchHistoryEnabled: getSettingsCollection(state).searchSettings.searchHistoryEnabled,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCoveoHooks;

export interface SearchHistoryStyles {
    headerText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    itemWrapper?: InjectableCss;
    itemWrapperInner?: InjectableCss;
    iconSearch?: InjectableCss;
    icon?: IconPresentationProps;
    link?: LinkPresentationProps;
    focusedLink?: LinkPresentationProps;
    clearLinkWrapper?: InjectableCss;
    clearLink?: LinkPresentationProps;
}

export const searchHistoryStyles: SearchHistoryStyles = {
    headerText: {
        weight: "bold",
        css: css`
            color: ${ADIColors.darkBlack3}!important;
            font-size: 16px;
            font-style: normal;
            font-weight: bold;
            line-height: normal;
            float: left;
            padding-bottom: 7px;
            width: 100%;
            ${({ theme }: { theme: BaseTheme; }) =>
                breakpointMediaQueries(theme, [
                    css`padding-bottom: 15px;`,
                    css`padding-bottom: 15px;`,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            
            width: 100%;
            padding: 0px;
            flex: 1;
            width: 100%;
            
            &:last-child:not(:only-child) {
            }

            ${({ theme }: { theme: BaseTheme; }) =>
                breakpointMediaQueries(theme, [
                    css`
                        border: none !important;
                        padding: 0px !important;
                        &:last-child:not(:only-child) {
                            flex: unset;
                            width: 100%;
                        }
                    `,
                    css`
                        border: none !important;
                        padding: 0px !important;
                        &:last-child:not(:only-child) {
                            flex: unset;
                            width: 100%;
                        }
                    `,
                    css`
                        border: none !important;
                        padding: 0px !important;
                        &:last-child:not(:only-child) {
                            flex: unset;
                        }
                    `,
                    css`
                        &:only-child {
                            width: 100%;
                        }
                        &:last-child:not(:only-child) {
                        }
                    `,
                    css`
                        &:only-child {
                            width: 100%;
                        }
                        &:last-child:not(:only-child) {
                        }
                    `,
                ])}
        `,
    },
    itemWrapper: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0 0 10px 0;
            ${({ theme }: { theme: BaseTheme; }) =>
                breakpointMediaQueries(theme, [
                    css`padding-bottom: 15px;`,
                    css`padding-bottom: 15px;`,
                    null,
                    null,
                    null,
                ])}
            &:last-child {
                padding-bottom: 0px!important;
            }
        `,
    },
    itemWrapperInner: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-direction: row;
            word-break: break-word;
            line-height: 1;
        `,
    },
    iconSearch: {
        css: css`
            margin-right: 7px;
            margin-bottom: 0px;
            top: 2px;
            position: relative;
        `,
    },
    icon: {
        src: Clock,
        css: css`
            margin-right: 7px;
            display: none;
            top: 2px;
            position: relative;
        `,
    },
    link: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
                color: ${getColor("text.main")} !important;
                font-weight: 600;
                font-size: 14px;
                line-height: normal !important;
                font-style: normal;
                cursor: pointer;
                white-space: normal;
                line-height: 1 !important;
                &:hover {
                    text-decoration: underline;
                }
            `,
        },
        css: css`
            width: auto;
            overflow: hidden;
            margin: 0;
        `,
    },
    focusedLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 5px 0;
            `,
        },
        css: css`
            width: 100%;
            overflow: hidden;
            background-color: lightgray;
        `,
    },
    clearLinkWrapper: {
        css: css`
            width: 100%;
            border-top: 1px solid ${ADIColors.gray3};
            margin-top: 5px;
            padding: 15px 0px 0px 0px;
            ${StyledButton} {
                margin-bottom: 0px;
            }
        `,
    },
    clearLink: {
        typographyProps: {
            css: css`
                width: 100%;
                color: ${getColor("primary")};
                text-align: left;
                font-size: 14px;
                font-weight: 700;
                margin-bottom: 0px;
                line-height: normal;
                &:hover {
                    text-decoration: underline !important;
                    color: ${ADIColors.blue5}!important;
                }
            `,
        },
    },
};

const styles = searchHistoryStyles;

const CoveoSearchHistory = ({
    searchHistoryEnabled,
    focusedQuery,
    clearSearch,
    closeAutocomplete,
}: Props) => {

    const { doCoveoSearch } = useCoveoSearch();

    const history = getSearchHistory();

    if (!searchHistoryEnabled) {
        return null;
    }

    const clearLinkClickHandler = () => {
        clearSearchHistory();
        closeAutocomplete();
    };

    if (history.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.headerText}>{translate("Search History")}</Typography>
            {history.map(search => {
                return (
                    <StyledWrapper {...styles.itemWrapper} key={search.query}>
                        <StyledWrapper {...styles.itemWrapperInner}>
                            <ButtonIcon
                                color={ADIColors.darkBlack3}
                                size={12}
                                src={SearchIcon}
                                {...styles.iconSearch}
                            />
                            <Link
                                {...(focusedQuery === search.query ? styles.focusedLink : styles.link)}
                                onClick={() => {
                                    doCoveoSearch(search.query);
                                    clearSearch();
                                    closeAutocomplete();
                                }}
                            >
                                {search.query}
                            </Link>
                        </StyledWrapper>
                    </StyledWrapper>
                )
            })}
            <StyledWrapper {...styles.clearLinkWrapper}>
                <Link {...styles.clearLink} onClick={clearLinkClickHandler}>
                    {translate("Clear Search History")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
}

export default connect(mapStateToProps)(withCoveoHooks(CoveoSearchHistory));
